/**
 * MED-921 - filtering and paging the invoices on a patient's record.
 *
 * Done in the browser rather than by the API, and that is a measurement rather than a shortcut.
 * The mean patient has 1.64 invoices and the busiest in the estate has 117, so the whole list is
 * already in the patient response and costs nothing to hold.
 *
 * The alternative would be fetching `/invoices?patient=X`, which is actively wrong here:
 * `InvoiceForCurrentUserByRoleExtension` scopes that collection to `assignedTo = current_user`
 * for a staff user, so it answers "my work queue for this patient" rather than "this patient's
 * invoices". The whole point of the register is to show all of them.
 *
 * Kept out of the component so it is testable - the jsdom suites in this repo cannot run.
 */

export const INVOICES_UNPAID = 'unpaid';
export const INVOICES_ALL = 'all';

/**
 * Eleven patients in the estate have more than 30 invoices and one has 117. This exists for
 * those, not for the common case.
 */
export const PER_PAGE = 20;

/**
 * "Unpaid" means still owed - not merely "not marked paid".
 *
 * Void and written-off invoices are closed: nobody is going to collect them. On the production
 * copy there are 7,110 void and 891 written off against 1,381 genuinely open, so treating them as
 * unpaid would bury the number a member of staff is actually looking for under eight thousand
 * that need nothing.
 *
 * Escalated stays in - escalation is a chasing state, which is the opposite of closed.
 */
export const isUnpaid = (invoice) => {
  if (!invoice) {
    return false;
  }

  return !invoice.isPaid && !invoice.isVoid && !invoice.isWrittenOff;
};

export const filterPatientInvoices = (invoices, mode = INVOICES_UNPAID) => {
  if (!Array.isArray(invoices)) {
    return [];
  }

  return mode === INVOICES_ALL ? [...invoices] : invoices.filter(isUnpaid);
};

export const pageCountFor = (invoices) => {
  const total = Array.isArray(invoices) ? invoices.length : 0;

  // Never zero: the paginator needs a valid page count even with nothing to show.
  return Math.max(1, Math.ceil(total / PER_PAGE));
};

/**
 * One page of invoices, with the page number clamped into range.
 *
 * Clamping matters when the mode changes: switching from All to Unpaid can leave the current page
 * past the end of a much shorter list, and an empty table there would read as "this patient has
 * no unpaid invoices" when they may have several on page one.
 */
export const pageOfInvoices = (invoices, page) => {
  if (!Array.isArray(invoices) || invoices.length === 0) {
    return [];
  }

  const clamped = Math.min(Math.max(1, Number(page) || 1), pageCountFor(invoices));
  const start = (clamped - 1) * PER_PAGE;

  return invoices.slice(start, start + PER_PAGE);
};
